/**
 * /dashboard/travel-preapprovals — Me → Expenses → Travel Requests: the
 * caller's own pre-approvals (G15).
 *
 * Wave 3 (nav-build spec decisions 2/5): approvals live only in the Inbox.
 * The approver's "Approvals" tab here duplicated Inbox → Approvals →
 * Travel (which also has History) and opened approvers on it by default,
 * so it is gone. An old `?tab=approvals` link goes to that Inbox tab.
 *
 * Top-right CTA → /dashboard/travel-preapprovals/new.
 *
 * Replaces the previous <ComingSoon> stub at this URL (G15 frontend).
 */

import { createFileRoute, Link, Navigate, useParams } from "@tanstack/react-router";
import { z } from "zod";
import { PlaneTakeoff, Plus } from "lucide-react";
import { PageHeader } from "@/components/shared/page-header";
import { TravelPreapprovalList } from "@/components/travel/travel-preapproval-list";
import { useTravelRoles } from "@/components/travel/use-travel-roles";
import { Button } from "@/components/ui/button";

// `tab` is kept only so old ?tab=approvals links still resolve.
const travelSearchSchema = z.object({
  tab: z.enum(["mine", "approvals"]).optional().catch(undefined),
});

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/travel-preapprovals/",
)({
  validateSearch: travelSearchSchema,
  component: TravelPreapprovalsHubRoute,
});

function TravelPreapprovalsHubRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { isApprover } = useTravelRoles();
  const search = Route.useSearch();

  // The approval queue moved to the Inbox (a non-approver just sees their
  // own list).
  if (search.tab === "approvals" && isApprover) {
    return (
      <Navigate
        to="/app/$tenantSlug/approvals"
        params={{ tenantSlug }}
        search={{ tab: "travel" }}
        replace
      />
    );
  }

  return (
    <>
      <PageHeader
        title="Travel Requests"
        description="Request manager / HR approval for a trip before you book it."
        actions={
          <Button asChild className="gap-1.5">
            <Link to="/app/$tenantSlug/travel-preapprovals/new" params={{ tenantSlug }}>
              <Plus className="h-4 w-4" />
              New Travel Pre-Approval
            </Link>
          </Button>
        }
      />

      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground sm:hidden">
          <PlaneTakeoff aria-hidden className="h-4 w-4" />
          <span>My pre-approvals</span>
        </div>
        <TravelPreapprovalList mode="self" />
      </div>
    </>
  );
}
